/**
 * #440/#441: the file for the insurer. Format, statuses (default Open, Raised,
 * Insurer rejected), dates, a count of what will be in it and, separately and
 * only when ticked, "mark exported Open lines as Raised". Downloading alone
 * changes nothing.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, Modal } from '@/components/common';
import { getErrorMessage } from '@/services';
import { statementVerificationService } from '@/services/statementVerification';
import type { DisputeStatus } from '@/services/statementVerification';
import { DISPUTE_LABELS, DISPUTE_ORDER } from './statementLabels';

const DEFAULT_STATUSES: DisputeStatus[] = ['open', 'raised', 'insurer_rejected'];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  insurer?: string;
  batch?: string;
}

export function StatementExportDialog({ isOpen, onClose, insurer, batch }: Props) {
  const qc = useQueryClient();
  const [format, setFormat] = useState<'csv' | 'xlsx'>('csv');
  const [statuses, setStatuses] = useState<DisputeStatus[]>(DEFAULT_STATUSES);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [markRaised, setMarkRaised] = useState(false);
  const [busy, setBusy] = useState(false);

  const filters = {
    insurer,
    batch,
    dispute_status: statuses.join(','),
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  };

  // The same list endpoint and filters as the screen, so the count is the file's count.
  const preview = useQuery({
    queryKey: ['statementExportPreview', filters],
    queryFn: () =>
      statementVerificationService.listLines({
        insurer,
        batch,
        dispute_status: statuses.join(',') || 'none-selected',
        page: 1,
      }),
    enabled: isOpen && statuses.length > 0 && !dateFrom && !dateTo,
  });
  const count = preview.data?.count;

  const toggle = (s: DisputeStatus) =>
    setStatuses((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const run = async () => {
    setBusy(true);
    try {
      const blob = await statementVerificationService.exportDisputes(filters, format);
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `statement-disputes.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
      if (markRaised) {
        const res = await statementVerificationService.markExportedRaised(filters);
        toast.success(`${res.raised} Open dispute${res.raised === 1 ? '' : 's'} marked Raised`);
        qc.invalidateQueries({ queryKey: ['statementLines'] });
        qc.invalidateQueries({ queryKey: ['statementDisputeSummary'] });
        qc.invalidateQueries({ queryKey: ['statementBatches'] });
      }
      onClose();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export disputes for the insurer" size="lg">
      <div className="space-y-4 text-sm">
        <fieldset>
          <legend className="mb-1 font-medium text-gray-900 dark:text-gray-100">Format</legend>
          <label className="mr-4">
            <input type="radio" name="fmt" checked={format === 'csv'} onChange={() => setFormat('csv')} /> CSV
          </label>
          <label>
            <input type="radio" name="fmt" checked={format === 'xlsx'} onChange={() => setFormat('xlsx')} /> Excel
          </label>
        </fieldset>
        <fieldset>
          <legend className="mb-1 font-medium text-gray-900 dark:text-gray-100">Dispute status</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {DISPUTE_ORDER.map((s) => (
              <label key={s}>
                <input type="checkbox" checked={statuses.includes(s)} onChange={() => toggle(s)} />{' '}
                {DISPUTE_LABELS[s]}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex flex-wrap gap-4">
          <label>
            Opened from{' '}
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded border border-gray-300 p-1 dark:border-gray-600 dark:bg-gray-800" />
          </label>
          <label>
            to{' '}
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded border border-gray-300 p-1 dark:border-gray-600 dark:bg-gray-800" />
          </label>
        </div>
        <p className="text-gray-600 dark:text-gray-300" aria-live="polite">
          {statuses.length === 0
            ? 'Choose at least one status.'
            : count !== undefined
              ? `${count} line${count === 1 ? '' : 's'} will be in the file.`
              : dateFrom || dateTo
                ? 'The count is shown when no dates are set; the file applies them.'
                : 'Counting…'}
        </p>
        <label className="flex items-start gap-2">
          <input type="checkbox" checked={markRaised} onChange={(e) => setMarkRaised(e.target.checked)} />
          <span>
            Mark exported Open lines as Raised (a separate step; each gets a system note). Downloading alone
            changes nothing.
          </span>
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={run} disabled={busy || statuses.length === 0 || count === 0}>
            {busy ? 'Preparing…' : 'Download'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
